import { Link, createFileRoute, notFound } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { authClient } from '../../auth/client'
import { SiteHeader } from '../../components/SiteHeader'
import { NameBoard } from '../../components/account/NameBoard'
import { CompleteSheet } from '../../components/cafe/CompleteSheet'
import { EntryBoard } from '../../components/cafe/EntryBoard'
import { NotificationsLink } from '../../components/cafe/NotificationsLink'
import { RecentReceipts } from '../../components/cafe/RecentReceipts'
import { RestChat } from '../../components/cafe/RestChat'
import { Timer } from '../../components/cafe/Timer'
import { TodayMenu } from '../../components/cafe/TodayMenu'
import { useFocusTimer } from '../../components/cafe/use-focus-timer'
import { ReceiptPreview } from '../../components/receipt/ReceiptPreview'
import { drawReceipt } from '../../components/receipt/draw-receipt'
import { CafeCanvas } from '../../components/scene/CafeCanvas'
import { useLocale } from '../../components/use-locale'
import { useMotionOff } from '../../components/use-motion-off'
import { useTimeOfDay } from '../../components/use-time-of-day'
import { fmt, getDict, parseLocale } from '../../i18n'
import { localHour } from '../../lib/date'
import { formatClock } from '../../lib/format'
import { type SavedLog, durationsOf } from '../../lib/timer'
import { seoHead } from '../../lib/seo'
import { SITE_ORIGIN } from '../../lib/site'
import { timeOfDayFromHour } from '../../lib/time-of-day'
import { mySeatOf } from '../../realtime/cafe-state'
import type { ClientMessage } from '../../realtime/protocol'
import { useCafeSocket } from '../../realtime/use-cafe-socket'
import type { SceneState } from '../../scene/cafe-scene'

export const Route = createFileRoute('/{-$locale}/cafe')({
  validateSearch: (s: Record<string, unknown>): { tod?: string } =>
    typeof s.tod === 'string' ? { tod: s.tod } : {},
  beforeLoad: ({ params }) => {
    if (!parseLocale(params.locale)) throw notFound()
  },
  loader: () => ({ serverTod: timeOfDayFromHour(localHour(Date.now(), 'Asia/Tokyo')) }),
  head: ({ params }) => {
    const locale = parseLocale(params.locale) ?? 'ja'
    const t = getDict(locale)
    return seoHead({
      origin: SITE_ORIGIN,
      locale,
      path: '/cafe',
      title: t.meta.cafeTitle,
      description: t.meta.cafeDescription,
    })
  },
  component: Cafe,
})

type AppUser = {
  id: string
  name: string
  image?: string | null
  isAnonymous?: boolean | null
  handle?: string | null
  displayName?: string | null
  timezone?: string | null
  locale?: string | null
}

type Receipt = { imageUrl: string; blob: Blob; publicUrl: string | null }

function Cafe() {
  const { serverTod } = Route.useLoaderData()
  const { tod: todParam } = Route.useSearch()
  const { t, locale, path } = useLocale()
  const tod = useTimeOfDay(serverTod, todParam)
  const session = authClient.useSession()
  const user = session.data?.user as AppUser | undefined
  // 表示名を決めるまでは席に着けない（店内からは見ているだけの人として扱う）
  const member = user?.displayName ? user : undefined
  const timer = useFocusTimer()
  const [motionOff, setMotionOff] = useMotionOff()
  const [receipt, setReceipt] = useState<Receipt | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const timerRef = useRef(timer.state)
  timerRef.current = timer.state
  const userRef = useRef(member)
  userRef.current = member

  // 再接続のたびに、手元のタイマーの状態を店へ送り直す
  const onOpen = useCallback((send: (m: ClientMessage) => void) => {
    if (!userRef.current) return
    const s = timerRef.current
    if (s.phase === 'focus')
      send({ type: 'focus:start', plannedMinutes: s.plannedMinutes, startedAt: s.startedAt })
    else if (s.phase === 'rest' || s.phase === 'done') send({ type: 'rest:start' })
  }, [])
  // 表示名が変わったら店に名乗り直す
  const socket = useCafeSocket(member ? `${member.id}:${member.displayName}` : 'viewer', onOpen)
  const { send } = socket
  const mySeat = mySeatOf(socket, user?.id)

  // 集中の満了と休憩の終わりを店に伝える
  const prevPhase = useRef(timer.state.phase)
  useEffect(() => {
    const prev = prevPhase.current
    prevPhase.current = timer.state.phase
    if (!timer.hydrated || prev === timer.state.phase) return
    if (prev === 'focus' && timer.state.phase === 'done') send({ type: 'focus:complete' })
    if (prev === 'rest' && timer.state.phase === 'idle') send({ type: 'focus:cancel' })
  }, [timer.state.phase, timer.hydrated, send])

  // タイムゾーンと言語をアカウントに反映する（日次集計の日付境界に使う）
  useEffect(() => {
    if (!user) return
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (user.timezone !== tz || user.locale !== locale) {
      void authClient.updateUser({ timezone: tz, locale })
    }
  }, [user?.id, user?.timezone, user?.locale, locale])

  // タブのタイトルに残り時間を出す
  useEffect(() => {
    if (timer.state.phase !== 'focus' && timer.state.phase !== 'rest') {
      document.title = t.meta.cafeTitle
      return
    }
    const label = timer.state.phase === 'focus' ? t.cafe.focusPhase : t.cafe.restPhase
    document.title = `${formatClock(timer.remaining)} ${label}｜Focus Cafe`
  }, [timer.remaining, timer.state.phase, t])

  const start = () => {
    if (!member) return
    timer.start()
    send({ type: 'focus:start', plannedMinutes: durationsOf(timer.state).focus })
  }
  const cancel = () => {
    timer.cancel()
    send({ type: 'focus:cancel' })
  }
  const closeReceipt = () => {
    if (receipt) URL.revokeObjectURL(receipt.imageUrl)
    setReceipt(null)
  }
  const toRest = () => {
    timer.rest()
    send({ type: 'rest:start' })
    closeReceipt()
  }
  // 休憩を挟まずに、同じ時間でもう一度集中する
  const focusAgain = () => {
    start()
    closeReceipt()
  }
  const linkGoogle = () => {
    void authClient.signIn.social({ provider: 'google', callbackURL: location.pathname })
  }
  const postChat = (text: string) => send({ type: 'chat:post', text })
  const deleteChat = (id: string) => send({ type: 'chat:delete', id })
  const signOut = async () => {
    await authClient.signOut()
    timer.cancel()
    location.reload()
  }

  // 記録済みの回からレシートを描く。受け取った直後と、店に入り直したときの両方で使う
  const showReceipt = useCallback(
    async (s: { startedAt: number; endedAt: number; plannedMinutes: number }, saved: SavedLog) => {
      const blob = await drawReceipt({
        locale,
        id: saved.publicUrl ? saved.id : null,
        startedAt: s.startedAt,
        endedAt: s.endedAt,
        plannedMinutes: s.plannedMinutes,
        focusSeconds: saved.focusSeconds,
        note: saved.note,
        name: member?.displayName ?? t.receipt.guest,
        seat: saved.seat,
      })
      setReceipt({ imageUrl: URL.createObjectURL(blob), blob, publicUrl: saved.publicUrl })
      return blob
    },
    [locale, member?.displayName, t],
  )

  const save = async (note: string) => {
    const s = timer.state
    if (s.phase !== 'done' || !member) return
    setSaving(true)
    setSaveError(null)
    try {
      const res = await fetch('/api/logs', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          startedAt: s.startedAt,
          endedAt: s.endedAt,
          plannedMinutes: s.plannedMinutes,
          note,
        }),
      })
      if (!res.ok) throw new Error(`POST /api/logs ${res.status}`)
      const log = (await res.json()) as { id: string; focusSeconds: number; publicUrl: string | null }
      const saved: SavedLog = {
        id: log.id,
        publicUrl: log.publicUrl,
        focusSeconds: log.focusSeconds,
        note: note.trim(),
        seat: mySeat,
      }
      // 記録したことを覚えておき、店に入り直しても受け取り直させない
      timer.markSaved(saved)
      const blob = await showReceipt(s, saved)
      // 新しく記録したときだけカードを載せる（送り直しで既存の記録が返ったときは載せ済み）
      if (log.publicUrl && res.status === 201) {
        // カードの保存に失敗しても、記録自体は済んでいるので先へ進める
        await fetch(`/api/logs/${log.id}/card`, {
          method: 'PUT',
          headers: { 'content-type': 'image/png' },
          body: blob,
        }).catch(() => {})
      }
    } catch (e) {
      console.error('[cafe] 記録に失敗しました', e)
      setSaveError(t.cafe.saveFailed)
    } finally {
      setSaving(false)
    }
  }

  // 記録済みの回で店に入り直したら、同じレシートを描き直して見せる
  const restoring = useRef(false)
  const [restoreFailed, setRestoreFailed] = useState(false)
  const doneState = timer.state.phase === 'done' ? timer.state : null
  const savedLog = doneState?.saved
  useEffect(() => {
    if (!doneState || !savedLog || !member || receipt || saving || restoring.current) return
    restoring.current = true
    showReceipt(doneState, savedLog)
      .catch((e) => {
        // 描けなければ受け取りの札に戻す。送り直してもサーバーは前の記録を返す
        console.error('[cafe] レシートを描き直せませんでした', e)
        setRestoreFailed(true)
      })
      .finally(() => {
        restoring.current = false
      })
  }, [doneState, savedLog, member, receipt, saving, showReceipt])

  const active = timer.state.phase !== 'idle'
  const scene = useMemo<SceneState>(
    () => ({
      seats: socket.seats.flatMap((s, seat) =>
        s ? [{ seat, status: s.status, mine: s.userId === user?.id }] : [],
      ),
      timeOfDay: tod,
      stars: socket.stars,
      mySeat,
      view: active && mySeat !== null ? 'seat' : 'exterior',
    }),
    [socket.seats, socket.stars, tod, mySeat, active, user?.id],
  )
  const celebrate = useMemo(
    () => (socket.lastStar ? { key: socket.lastStar.seq, seat: socket.lastStar.seat } : undefined),
    [socket.lastStar],
  )

  const minutes = timer.state.phase === 'idle' ? durationsOf(timer.state).focus : timer.state.plannedMinutes
  const loading = session.isPending || !timer.hydrated

  let stage: React.ReactNode = null
  // セッションの確認中も場所を確保して、入店ボードが現れたときにレイアウトがずれないようにする
  if (loading) stage = <div className="cafe__placeholder" aria-hidden="true" />
  else if (!user) stage = <EntryBoard onEntered={() => session.refetch()} />
  else if (!member) stage = <NameBoard onSaved={() => session.refetch()} />
  else if (timer.state.phase === 'done' && timer.state.saved && !receipt && !restoreFailed)
    // 記録済みのレシートを描き直しているあいだは、受け取りの札を出さない
    stage = <div className="cafe__placeholder" aria-hidden="true" />
  else if (timer.state.phase === 'done')
    stage = receipt ? (
      <ReceiptPreview
        imageUrl={receipt.imageUrl}
        blob={receipt.blob}
        publicUrl={receipt.publicUrl}
        minutes={minutes}
        isGuest={Boolean(user.isAnonymous)}
        onRest={toRest}
        onFocusAgain={focusAgain}
        onLinkGoogle={linkGoogle}
      />
    ) : (
      <CompleteSheet
        minutes={minutes}
        saving={saving}
        error={saveError}
        isGuest={Boolean(user.isAnonymous)}
        onSave={save}
      />
    )
  else
    stage = (
      <Timer
        state={timer.state}
        remaining={timer.remaining}
        disabled={!socket.connected}
        onSelect={timer.select}
        onCustom={timer.setCustom}
        onStart={start}
        onCancel={cancel}
        onFinishRest={timer.finishRest}
      />
    )

  const status = !socket.connected
    ? t.cafe.reconnecting
    : active && member
      ? mySeat !== null
        ? fmt(t.cafe.seatLabel, { n: mySeat + 1 })
        : // 席の割り当てを待つあいだは何も出さない。テラスに誰かいるときだけ満席と伝える
          socket.terrace > 0
          ? t.cafe.terraceSeat
          : null
      : !user && !loading
        ? t.cafe.viewerNote
        : null

  return (
    <div className={`cafe cafe--${timer.state.phase} cafe--${tod}`}>
      <SiteHeader showEnter={false} />
      <CafeCanvas
        state={scene}
        celebrate={celebrate}
        className="cafe__scene"
        label={t.meta.cafeDescription}
        motionOff={motionOff}
      />
      <div className="cafe__veil" aria-hidden="true" />
      <main id="main" className="cafe__ui">
        <h1 className="sr-only">{t.meta.cafeTitle}</h1>
        <div className="cafe__stage">
          {/* 集中しているあいだは出さない。始める前と休憩中に、みんなのがんばりを眺められる */}
          {!loading && (timer.state.phase === 'idle' || timer.state.phase === 'rest') && (
            <RecentReceipts canLike={Boolean(member)} refreshKey={`${user?.id}:${timer.state.phase}`} />
          )}
          {stage}
          {/* 休憩中だけ、休憩している人どうしで話せる。書けるのは Google と連携した人だけ */}
          {timer.state.phase === 'rest' && socket.chat && (
            <RestChat
              messages={socket.chat}
              myId={user?.id}
              canPost={Boolean(member && !member.isAnonymous)}
              onPost={postChat}
              onDelete={deleteChat}
              onLinkGoogle={linkGoogle}
            />
          )}
          {status && (
            <p className="cafe__status" role="status">
              {status}
            </p>
          )}
          <label className="motion-switch">
            <input
              type="checkbox"
              role="switch"
              checked={motionOff}
              onChange={(e) => setMotionOff(e.target.checked)}
            />
            <span className="motion-switch__track" aria-hidden="true" />
            <span className="motion-switch__label">{t.cafe.motionOff}</span>
          </label>
        </div>
        <TodayMenu
          seats={socket.seats}
          terrace={socket.terrace}
          visitors={socket.present}
          stars={socket.stars}
          myId={user?.id}
          footer={
            user && (
              <div className="today-menu__account">
                <hr className="chalk-rule" />
                {user.isAnonymous && (
                  <button type="button" className="btn btn--chalk btn--small btn--block" onClick={linkGoogle}>
                    {t.auth.linkGoogle}
                  </button>
                )}
                <NotificationsLink />
                <Link to={path('/me')} className="today-menu__link">
                  {t.nav.profile}
                </Link>
                <button type="button" className="today-menu__link" onClick={signOut}>
                  {t.nav.signOut}
                </button>
              </div>
            )
          }
        />
      </main>
    </div>
  )
}
